import { Button, Text, useToast } from '@chakra-ui/react';
import { StudentEmployerPlacementStatus } from '@duke-hq/svc-api-db/client';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { SemesterCompletionAlert } from '../../components/SemesterCompletionAlert';
import { testIds } from './testIds';

export type PlacementActionStatus =
  | typeof StudentEmployerPlacementStatus.AWAITING_EVIDENCE
  | typeof StudentEmployerPlacementStatus.COMPLETED
  | typeof StudentEmployerPlacementStatus.FAILED
  | typeof StudentEmployerPlacementStatus.CANCELLED;

const storedStageReason: Record<PlacementActionStatus, string> = {
  [StudentEmployerPlacementStatus.AWAITING_EVIDENCE]: 'Evidence requested from the student.',
  [StudentEmployerPlacementStatus.COMPLETED]: 'Placement marked complete.',
  [StudentEmployerPlacementStatus.FAILED]: 'Placement marked failed.',
  [StudentEmployerPlacementStatus.CANCELLED]: 'Placement cancelled.',
};

function actionCopy(status: PlacementActionStatus): {
  title: string;
  body: string;
  confirm: string;
} {
  switch (status) {
    case StudentEmployerPlacementStatus.AWAITING_EVIDENCE:
      return {
        title: t`Request evidence`,
        body: t`Move this placement to awaiting evidence so the student can submit their logbook.`,
        confirm: t`Request evidence`,
      };
    case StudentEmployerPlacementStatus.COMPLETED:
      return {
        title: t`Mark placement complete`,
        body: t`Confirm the student has finished this placement.`,
        confirm: t`Mark complete`,
      };
    case StudentEmployerPlacementStatus.FAILED:
      return {
        title: t`Mark placement failed`,
        body: t`Record that this placement was not completed successfully.`,
        confirm: t`Mark failed`,
      };
    case StudentEmployerPlacementStatus.CANCELLED:
      return {
        title: t`Cancel placement`,
        body: t`End this placement. The student returns to Sourcing and can be placed again once the stage is moved to Contracts.`,
        confirm: t`Cancel placement`,
      };
  }
}

export function PlacementActionModal({
  isOpen,
  onClose,
  employerPlacementId,
  targetStatus,
  courseEnrolmentId,
  unitOfCompetencyIds,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  employerPlacementId: string;
  targetStatus: PlacementActionStatus;
  courseEnrolmentId: number;
  unitOfCompetencyIds: string[];
  onSuccess: () => void;
}): React.JSX.Element {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const copy = actionCopy(targetStatus);
  const isCompleting = targetStatus === StudentEmployerPlacementStatus.COMPLETED;

  const { data: wouldCompleteCourseData } =
    trpcAdmin.enrolment.getWouldCompleteCourseOnWorkPlacement.useQuery(
      { courseEnrolmentId, unitOfCompetencyIds },
      { enabled: isOpen && isCompleting },
    );

  const setEmployerPlacementStatusMutation =
    trpcAdmin.workPlacements.placements.setEmployerPlacementStatus.useMutation({
      onSuccess: () => {
        toast({ status: 'success', title: t`Placement updated` });
        onSuccess();
        onClose();
      },
      onError: (error) => {
        // The status change may have committed before a later step failed.
        void utils.workPlacements.placements.journeyByEnrolment.invalidate({ courseEnrolmentId });
        toast({
          status: 'error',
          title: t`Failed to update placement`,
          description: error.message,
        });
      },
    });

  return (
    <FormModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      modalContentHeight="auto"
      modalContent={
        <FormMainContainer>
          <FormContentContainer>
            <Text fontSize="lg" fontWeight="semibold" w="full">
              {copy.title}
            </Text>
            <Text fontSize="sm" color="fg.muted" w="full">
              {copy.body}
            </Text>
            {isCompleting && wouldCompleteCourseData?.wouldCompleteCourse === true ? (
              <SemesterCompletionAlert />
            ) : null}
          </FormContentContainer>

          <FormFooterContainer>
            <Button
              isLoading={setEmployerPlacementStatusMutation.isPending}
              onClick={() =>
                setEmployerPlacementStatusMutation.mutate({
                  employerPlacementId,
                  toStatus: targetStatus,
                  reason: storedStageReason[targetStatus],
                })
              }
              data-testid={testIds.placementActionConfirm}
            >
              {copy.confirm}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              <Trans>Back</Trans>
            </Button>
          </FormFooterContainer>
        </FormMainContainer>
      }
    />
  );
}
